import clsx from 'clsx';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { TimelineActual } from '../trip-timeline.api';
import { durationText } from '../policies/timeline-duration.policy';
import { milestoneLabel, sinceArrivalLabel } from '../policies/timeline-labels.policy';
import { clockText } from '../policies/timeline-time.policy';
import { varianceLabel, type VarianceTone } from '../policies/timeline-variance-label.policy';
import { SourceChips } from './SourceChips';

const TONE_CLASS: Record<VarianceTone, string> = { late: 'text-error', early: 'text-success', on_time: 'text-success' };

interface TimelineActualRowProps {
  actual: TimelineActual;
  timezone: string;
  /** Present on an arrival: when it was planned and how far off it was, so the row reads plan beside actual. */
  plan?: { plannedAt: string | null; varianceSeconds: number | null };
  /** Day-qualified time text, so a run over midnight reads right. */
  timeText: (iso: string) => string;
}

function Variance({ seconds }: { seconds: number | null }) {
  const label = varianceLabel(seconds);
  if (!label) return null;
  const Icon = label.tone === 'late' ? AlertTriangle : CheckCircle2;
  return (
    <span className={clsx('flex items-center gap-1 font-medium', TONE_CLASS[label.tone])}>
      <Icon className="size-4" aria-hidden /> {label.text}
    </span>
  );
}

/** One fact on a stop: what happened, when by the most trusted source, against the plan for an arrival, and who recorded it. */
export function TimelineActualRow({ actual, timezone, plan, timeText }: TimelineActualRowProps) {
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
      <span className="w-28 shrink-0 font-medium text-fg">{milestoneLabel(actual.milestone)}</span>
      {plan && <span className="text-muted">{plan.plannedAt ? `plan ${timeText(plan.plannedAt)}` : 'no planned time'}</span>}
      <span className="text-fg" title={`Recorded ${clockText(actual.serverAt, timezone)}`}>
        actual {timeText(actual.at)}
      </span>
      {plan && <Variance seconds={plan.varianceSeconds} />}
      {actual.secondsSinceArrival !== null && (
        <span className="text-muted">
          ({sinceArrivalLabel(actual.milestone)}: {durationText(actual.secondsSinceArrival)})
        </span>
      )}
      <SourceChips sources={actual.sources} timezone={timezone} />
    </li>
  );
}
